import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";
import { delay } from "../helper/delayresponse";

interface SmsGateways {
  id?: number;
  name?: string;
  slug?: string;
  gateway_id?: number;
  account_sid?: string;
  auth_token?: string;
  phone_numbers?: string;
  gateway?: {
    name: string;
  };
  sending_limit?: number | null;
  api_key?: string;
  public_key?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface SmsGatewaysState {
  smsgatewaysdata: SmsGateways[];
  info: {
    count: number;
    current_page: number;
    limit: number;
    total_page: number;
  } | null;
  error: string | null;
  loading: boolean;
}

// ====================listing-action-start==============
export const GetallSmsGateways = createAsyncThunk<
  {
    data: SmsGateways[];
    info: {
      count: number;
      current_page: number;
      limit: number;
      total_page: number;
    };
  },
  { search: string; page: number; limit: number },
  { rejectValue: string }
>("GetallSmsGateways", async ({ search, page, limit }, { rejectWithValue }) => {
  try {
    await delay(1000)
    const response = await axiosInstance.get(`api/admin/sms-gateways`, {
      params: { search, page, limit },
    });
    return { data: response.data.data, info: response.data.info };
  } catch (error: any) {
    if (error.response && error.response.data) {
      return rejectWithValue(error.response.data);
    }
    return rejectWithValue(error.message);
  }
});
// ====================listing-action-end==============

// ==================view-Smsgatewaylist-api-start=============
export const GetSmsGatewayView = createAsyncThunk<
  SmsGateways,
  number,
  { rejectValue: string }
>("smsgateways/GetSmsGatewayView", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`api/admin/sms-gateways/${id}`);
    return response.data.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return rejectWithValue(error.response.data);
    }
    return rejectWithValue(error.message);
  }
});
// ==================view-Smsgatewaylist-api-end=============

// ==================AddSmsgateways-action-start===========
export const AddSmsGateways = createAsyncThunk<
  SmsGateways,
  SmsGateways,
  { rejectValue: string }
>(
  "smsgatewaysdata/AddSmsGateways",
  async (smsGatewaysData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `api/admin/sms-gateways/create`,
        smsGatewaysData
      );
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
// ==================AddSmsgateways-action-end===========

// =================Updateagentmonitors-action-start=========
export const UpdateSmsgateways = createAsyncThunk<
  SmsGateways,
  { id: number; updatedData: SmsGateways },
  { rejectValue: string }
>(
  "smsgatewaysdata/UpdateSmsgateways",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/api/admin/sms-gateways/update/${id}`,
        updatedData
      );
      return response.data;
    } catch (error: any) {
      console.log(error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
// =================Updateagentmonitors-action-end=========

// ================Delete-agent/monitor-start=========
export const Deletesmsgateways = createAsyncThunk<
  { id: number },
  { id: number },
  { rejectValue: string }
>("smsgatewaysdata/Deletesmsgateways", async ({ id }) => {
  try {
    const { data } = await axiosInstance.delete(`api/admin/sms-gateways/${id}`);
    return data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return error.message;
  }
});
// ================Delete-agent/monitor-end=========

const initialState: SmsGatewaysState = {
  smsgatewaysdata: [],
  info: null,
  error: null,
  loading: false,
};

const smsgatewaysSlice = createSlice({
  name: "Smsgateways",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ===============get-all-agentmonitor-slice-start============
      .addCase(GetallSmsGateways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        GetallSmsGateways.fulfilled,
        (
          state,
          action: PayloadAction<{
            data: SmsGateways[];
            info: {
              count: number;
              current_page: number;
              limit: number;
              total_page: number;
            };
          }>
        ) => {
          state.loading = false;
          state.smsgatewaysdata = action.payload.data;
          state.info = action.payload.info;
        }
      )
      .addCase(
        GetallSmsGateways.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "An error occurred";
        }
      );
    // ===============get-all-agentmonitor-slice-end============

    // ==================view-smsgatewaylist-slice-start=============
    builder
      .addCase(GetSmsGatewayView.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        GetSmsGatewayView.fulfilled,
        (state, action: PayloadAction<SmsGateways>) => {
          state.loading = false;
          const index = state.smsgatewaysdata.findIndex(
            (item) => item.id === action.payload.id
          );
          if (index !== -1) {
            state.smsgatewaysdata[index] = action.payload;
          } else {
            state.smsgatewaysdata.push(action.payload);
          }
        }
      )
      .addCase(
        GetSmsGatewayView.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to fetch SMS Gateway details";
        }
      );
    // ==================view-smsgatewaylist-slice-end=============

    // =================add-agentmonitor-slice-start============
    builder
      .addCase(AddSmsGateways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        AddSmsGateways.fulfilled,
        (state, action: PayloadAction<SmsGateways>) => {
          state.loading = false;
          state.smsgatewaysdata.push(action.payload);
        }
      )
      .addCase(
        AddSmsGateways.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to add smsgateways";
        }
      );
    // =================add-agentmonitor-slice-end============

    // =================Update agent/monitor-slice-start============
    builder
      .addCase(UpdateSmsgateways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        UpdateSmsgateways.fulfilled,
        (state, action: PayloadAction<SmsGateways>) => {
          state.loading = false;
          const index = state.smsgatewaysdata.findIndex(
            (item) => item.id === action.payload.id
          );
          if (index !== -1) {
            state.smsgatewaysdata[index] = action.payload;
          }
        }
      )
      .addCase(
        UpdateSmsgateways.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update SmsGateways";
        }
      );
    // =================Update agent/monitor-slice-start============

    // =================Delete agent/monitor-slice-start============
    builder
      .addCase(Deletesmsgateways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(Deletesmsgateways.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(Deletesmsgateways.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete agent/monitor";
      });
    // =================Delete agent/monitor-slice-end============
  },
});

export default smsgatewaysSlice.reducer;
